'use client';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { Check, X, Calendar } from 'lucide-react';
import api from '@/lib/api';
import { notify } from '@/lib/toast';
import { getDisplayName } from '@/lib/displayName';
import { useAuthStore } from '@/store/auth.store';
import { trackBookingCreated } from '@/lib/analytics';
import { pluralReviews } from './StarRating';

/**
 * Запись в выдвижной панели (ТЗ 4). Раньше выбор даты и времени раскрывался
 * аккордеоном внутри карточки — отсюда обрезанные слоты в узких колонках,
 * прыгающая сетка и невозможность держать карточки одной высоты.
 *
 * Десктоп — панель справа, мобиль — bottom sheet. Один и тот же компонент
 * используется и в каталоге, и на странице профи.
 */

export interface DrawerService {
  id: string;
  title: string;
  price: number | string;
  durationMinutes?: number;
  priceType?: string;
  price_type?: string;
  format?: string;
  address?: string;
  payment_methods?: string[];
}

/**
 * Место и способы оплаты — вернулись из удалённой модалки подтверждения:
 * при записи из каталога это единственное место, где клиент узнаёт, куда
 * ехать и чем платить.
 */
const formatPaymentMethods = (methods?: string[]): string => {
  if (!methods?.length) return '';
  const map: Record<string, string> = { cash: 'наличные', card: 'карта', sbp: 'СБП', online: 'онлайн' };
  return methods.map(m => map[m] ?? m).join(', ');
};

const locationLine = (service: DrawerService, specialist: any): string => {
  const fmt = service.format ?? 'offline';
  if (fmt === 'online') return 'Ссылку на встречу профи пришлёт дополнительно';
  if (fmt === 'arrival') {
    return service.address
      ? 'Профи приедет по адресу: ' + service.address
      : 'Профи приедет к вам — адрес уточните при подтверждении';
  }
  const addr = service.address || specialist?.address;
  return addr ? 'Адрес: ' + addr : 'Адрес уточните у специалиста при подтверждении';
};

interface Props {
  open: boolean;
  onClose: () => void;
  specialistId: string;
  /** Для шапки: имя, аватар, рейтинг */
  specialist: any;
  services: DrawerService[];
  /** Предвыбранная услуга (клик по конкретной услуге в карточке) */
  initialServiceId?: string | null;
  /** Предвыбранная дата — из фильтра каталога */
  initialDate?: string;
}

const toLocalDate = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

function formatDateLabel(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00');
  const today = toLocalDate();
  const tomorrow = (() => {
    const t = new Date(); t.setDate(t.getDate() + 1);
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  })();
  if (dateStr === today) return 'Сегодня';
  if (dateStr === tomorrow) return 'Завтра';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', weekday: 'short' });
}

function formatDateShort(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00');
  const today = toLocalDate();
  return {
    day: d.getDate(),
    weekday: dateStr === today ? 'сег' : d.toLocaleDateString('ru-RU', { weekday: 'short' }),
  };
}

const getAvatarColor = (name: string) => {
  const colors = ['#307479', '#7c3aed', '#2563eb', '#e11d48', '#d97706', '#059669', '#4f46e5', '#0891b2'];
  return colors[name.charCodeAt(0) % colors.length];
};

export default function BookingDrawer({
  open, onClose, specialistId, specialist, services, initialServiceId, initialDate,
}: Props) {
  const router = useRouter();
  const { isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();

  const [serviceId, setServiceId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [comment, setComment] = useState('');

  // Сброс при каждом открытии: панель переиспользуется между карточками.
  useEffect(() => {
    if (!open) return;
    setServiceId(initialServiceId ?? (services.length === 1 ? services[0].id : null));
    setSelectedDate(null);
    setSelectedTime(null);
    setComment('');
  }, [open, initialServiceId, services]);

  // Пока панель открыта, фон не скроллится — иначе на мобиле уезжает страница.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const service = useMemo(
    () => services.find(s => s.id === serviceId) ?? null,
    [services, serviceId],
  );

  const availDateFrom = useMemo(() => toLocalDate(), []);
  const availDateTo = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + 30);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const { data: availData, isLoading } = useQuery({
    queryKey: ['avail', specialistId, serviceId, availDateFrom, availDateTo],
    queryFn: () =>
      api.get(`/specialists/${specialistId}/services/${serviceId}/availability`, {
        params: { date_from: availDateFrom, date_to: availDateTo },
      }).then(r => r.data.data),
    enabled: open && !!serviceId,
    staleTime: 60_000,
  });

  const availDates = useMemo(() => {
    if (!availData?.availability) return new Set<string>();
    return new Set<string>(availData.availability.map((a: { date: string }) => a.date));
  }, [availData]);

  // Дата из фильтра каталога — только если она доступна у этой услуги.
  useEffect(() => {
    if (initialDate && availDates.has(initialDate)) setSelectedDate(initialDate);
  }, [initialDate, availDates]);

  const slotsForDate = useMemo(() => {
    if (!selectedDate || !availData?.availability) return [];
    return availData.availability.find((a: { date: string }) => a.date === selectedDate)?.slots ?? [];
  }, [availData, selectedDate]);

  const calendarDates = useMemo(() => {
    const dates: string[] = [];
    const start = new Date(availDateFrom + 'T00:00:00');
    for (let i = 0; i < 30; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      dates.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    }
    return dates;
  }, [availDateFrom]);

  const bookingMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post('/bookings', data),
    onSuccess: () => {
      notify.success('Заявка отправлена! Ожидайте подтверждения от профи.');
      trackBookingCreated({ serviceTitle: service?.title ?? '' });
      queryClient.invalidateQueries({ queryKey: ['avail', specialistId] });
      onClose();
      router.push('/dashboard');
    },
    onError: (err: any) => {
      notify.error(err?.response?.data?.error?.message || 'Ошибка при бронировании');
    },
  });

  const handleBook = useCallback(() => {
    if (!isAuthenticated()) {
      router.push(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    if (!serviceId || !selectedDate || !selectedTime) {
      notify.error('Выберите услугу, дату и время');
      return;
    }
    bookingMutation.mutate({
      specialist_id: specialistId,
      service_id: serviceId,
      booking_date: selectedDate,
      start_time: selectedTime,
      ...(comment.trim() && { client_comment: comment.trim() }),
    });
  }, [isAuthenticated, router, serviceId, selectedDate, selectedTime, comment, bookingMutation, specialistId]);

  if (!open) return null;

  const displayName = getDisplayName(specialist?.user);
  const apiBase = process.env.NEXT_PUBLIC_API_URL?.replace('/api/v1', '') ?? '';
  const rawAvatar = specialist?.user?.avatarUrl;
  const avatarUrl = rawAvatar ? (rawAvatar.startsWith('http') ? rawAvatar : apiBase + rawAvatar) : null;

  return (
    <div
      onClick={e => { e.preventDefault(); e.stopPropagation(); onClose(); }}
      className="fixed inset-0 z-[700] bg-black/50 flex items-end sm:items-stretch sm:justify-end"
      role="dialog"
      aria-modal="true"
      aria-label={`Запись к ${displayName}`}
    >
      <div
        onClick={e => { e.preventDefault(); e.stopPropagation(); }}
        className="bg-bg-surface w-full sm:w-[440px] sm:max-w-full max-h-[88vh] sm:max-h-none sm:h-full rounded-t-2xl sm:rounded-none flex flex-col shadow-2xl"
      >
        {/* Шапка: аватар, имя, рейтинг */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border-subtle flex-shrink-0">
          {avatarUrl ? (
            <img src={avatarUrl} alt={displayName} className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
              style={{ background: getAvatarColor(displayName) }}>
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-text-primary text-sm truncate">{displayName}</div>
            {Number(specialist?.rating) > 0 && (
              <div className="text-xs text-text-muted">
                <span style={{ color: '#edb84a' }}>★</span>{' '}
                {Number(specialist.rating).toFixed(1).replace('.', ',')}
                {specialist.reviewsCount > 0 && ` · ${specialist.reviewsCount} ${pluralReviews(specialist.reviewsCount)}`}
              </div>
            )}
          </div>
          <button onClick={onClose} aria-label="Закрыть"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-text-secondary hover:bg-bg-muted flex-shrink-0">
            <X size={17} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-4 py-4 space-y-4">

          {/* Выбор услуги — только когда их больше одной */}
          {services.length > 1 && (
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Услуга</p>
              <div className="space-y-1.5">
                {services.map(s => {
                  const isSel = s.id === serviceId;
                  const priceNum = Number(s.price);
                  const priceStr = isNaN(priceNum) ? String(s.price) : priceNum.toLocaleString('ru-RU') + ' ₽';
                  const hasFrom = s.priceType === 'from' || s.price_type === 'from';
                  return (
                    <button key={s.id} type="button"
                      onClick={() => { setServiceId(s.id); setSelectedDate(null); setSelectedTime(null); }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border text-left transition-all"
                      style={isSel
                        ? { borderColor: 'var(--color-brand)', backgroundColor: 'rgba(48,116,121,0.08)' }
                        : { borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-text-primary truncate">{s.title}</div>
                        {s.durationMinutes && (
                          <div className="text-[11px] text-text-muted mt-0.5">{s.durationMinutes} мин</div>
                        )}
                      </div>
                      <span className="text-sm font-semibold text-brand whitespace-nowrap flex-shrink-0">
                        {hasFrom ? 'от ' : ''}{priceStr}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {!serviceId && services.length > 1 && (
            <p className="text-xs text-gray-400 text-center">Выберите услугу выше</p>
          )}

          {serviceId && isLoading && (
            <div className="space-y-2">
              {[1, 2].map(i => <div key={i} className="h-11 bg-bg-muted animate-pulse rounded-xl" />)}
            </div>
          )}

          {serviceId && !isLoading && availDates.size === 0 && (
            <div className="flex flex-col items-center gap-3 py-6">
              <div className="w-10 h-10 rounded-full bg-bg-muted flex items-center justify-center">
                <Calendar size={18} className="text-gray-400" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-500">Нет доступных слотов</p>
                <p className="text-xs text-gray-400 mt-0.5">На ближайшие 30 дней запись закрыта</p>
              </div>
            </div>
          )}

          {serviceId && !isLoading && availDates.size > 0 && (
            <>
              {/* Лента дат — ширина панели позволяет показать их без обрезки */}
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Выберите день</p>
                <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
                  {calendarDates.filter(d => availDates.has(d)).map(dateStr => {
                    const isSel = selectedDate === dateStr;
                    const { day, weekday } = formatDateShort(dateStr);
                    return (
                      <button key={dateStr} type="button"
                        onClick={() => { setSelectedDate(dateStr); setSelectedTime(null); }}
                        className="flex-shrink-0 flex flex-col items-center px-2.5 py-2 rounded-xl text-xs font-medium transition-all border min-w-[48px]"
                        style={isSel
                          ? { backgroundColor: 'var(--color-brand)', color: '#fff', borderColor: 'var(--color-brand)' }
                          : { backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-brand)', borderColor: 'rgba(48,116,121,0.19)' }}>
                        <span className="text-[10px] mb-0.5 opacity-70">{weekday}</span>
                        <span className="text-sm font-bold">{day}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Слоты переносятся на несколько строк, а не скроллятся с обрезкой */}
              {selectedDate && (
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    {formatDateLabel(selectedDate)}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {slotsForDate.map((time: string) => (
                      <button key={time} type="button" onClick={() => setSelectedTime(time)}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium border transition-all"
                        style={selectedTime === time
                          ? { backgroundColor: 'var(--color-brand)', color: '#fff', borderColor: 'var(--color-brand)' }
                          : { borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-surface)' }}>
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!selectedDate && <p className="text-xs text-gray-400 text-center">Выберите удобный день выше</p>}
              {selectedDate && !selectedTime && <p className="text-xs text-gray-400 text-center">Выберите удобное время</p>}

              {selectedDate && selectedTime && service && (
                <div className="space-y-3">
                  <div className="bg-brand/8 border border-brand/20 rounded-xl px-3.5 py-2.5 space-y-1">
                    <div className="font-medium text-text-primary text-sm">{service.title}</div>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        {formatDateLabel(selectedDate)}, {selectedTime}
                      </div>
                      <span className="font-bold text-brand text-base ml-3 whitespace-nowrap">
                        {Number(service.price).toLocaleString('ru-RU')} ₽
                      </span>
                    </div>
                  </div>

                  {/* Место, формат и оплата — до отправки заявки, а не после */}
                  <div className="space-y-1.5 text-xs text-text-secondary">
                    <div className="flex gap-1.5">
                      <span className="flex-shrink-0">📍</span>
                      <span>{locationLine(service, specialist)}</span>
                    </div>
                    {formatPaymentMethods(service.payment_methods) && (
                      <div className="flex gap-1.5">
                        <span className="flex-shrink-0">💳</span>
                        <span>Оплата: {formatPaymentMethods(service.payment_methods)}</span>
                      </div>
                    )}
                    {service.durationMinutes && (
                      <div className="flex gap-1.5">
                        <span className="flex-shrink-0">⏱</span>
                        <span>{service.durationMinutes} мин</span>
                      </div>
                    )}
                    <div className="flex gap-1.5 text-gray-400">
                      <span className="flex-shrink-0">⏳</span>
                      <span>Заявка ждёт подтверждения профи — вы получите уведомление</span>
                    </div>
                  </div>

                  <textarea
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    placeholder="Комментарий для специалиста (необязательно)"
                    maxLength={500}
                    rows={2}
                    className="w-full border border-border rounded-xl px-3 py-2.5 text-sm text-text-ui placeholder-gray-400 focus:border-brand focus:ring-2 focus:ring-brand/20 resize-none transition-all bg-bg-surface"
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Кнопка закреплена внизу панели — не теряется при длинном списке слотов */}
        {serviceId && availDates.size > 0 && (
          <div className="px-4 py-3 border-t border-border-subtle flex-shrink-0">
            <button onClick={handleBook}
              disabled={bookingMutation.isPending || !selectedDate || !selectedTime}
              className="w-full py-3 rounded-xl font-semibold text-sm text-white flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ backgroundColor: 'var(--color-brand)' }}>
              <Check size={16} />
              {bookingMutation.isPending ? 'Бронируем...' : 'Оставить заявку'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
